import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { Project } from '@shared/models';

import { ProjectService } from './http-client/project.service';

@Injectable({
  providedIn: 'root',
})
export class TitleService {
  items: MenuItem[] = [];
  items$ = new BehaviorSubject<MenuItem[]>(this.items);
  title = 'Stay Ahead';
  title$ = new BehaviorSubject<string>(this.title);

  constructor(
    private router: Router,
    private titleService: Title,
    private translate: TranslateService,
    private projectService: ProjectService
  ) {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const root = this.router.routerState.snapshot.root;
      const projectId = this.projectIdFromRoute(root);

      if (projectId) {
        this.projectService.getByIdWithIncludes('', projectId, { includes: ['details'] }).subscribe((project) => {
          this.setMenuItems(root, project);
        });
      } else {
        this.setMenuItems(root);
      }
    });
  }

  setMenuItems(route: ActivatedRouteSnapshot, project?: Project) {
    const breadcrumbs: MenuItem[] = [];
    this.addBreadcrumb(route, [], breadcrumbs, project);
    this.items = breadcrumbs;
    this.items$.next(this.items);

    this.title = `Stay Ahead | ${breadcrumbs.map((x) => x.title).join(' | ')}`;
    this.title$.next(this.title);
    this.titleService.setTitle(this.title);
  }

  projectIdFromRoute(route: ActivatedRouteSnapshot) {
    let projectId = route.paramMap.get('projectId');
    if (!projectId && route.firstChild) projectId = this.projectIdFromRoute(route.firstChild);

    return projectId;
  }

  private addBreadcrumb(
    route: ActivatedRouteSnapshot,
    parentUrl: string[],
    breadcrumbs: MenuItem[],
    project?: Project
  ): void {
    const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
    const breadcrumb = route.data['breadcrumb'];
    const parentBreadcrumb = route.parent && route.parent.data ? route.parent.data['breadcrumb'] : null;

    if (breadcrumb && breadcrumb !== parentBreadcrumb) {
      const [label, title, toTranslate] = this.setLabel(breadcrumb, project);

      breadcrumbs.push({
        label: label,
        title: title,
        url: '/' + routeUrl.join('/'),
        route: routeUrl,
        target: '_self',
        toTranslate: toTranslate,
      });
    }

    if (route.firstChild) {
      this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs, project);
    }
  }

  setLabel(breadcrumb: string, project?: Project): [string, string, boolean] {
    let label = breadcrumb;

    const keysToRaplace = {
      '{projectName}': project?.details?.name,
      'breadcrumb.constructionAreas': project?.settings?.constructionAreasAlias,
      'breadcrumb.activities': project?.settings?.activitiesAlias,
      'breadcrumb.phases': project?.settings?.phasesAlias,
    };

    const key = Object.keys(keysToRaplace).find((x) => label.includes(x));
    let toTranslate = true;

    if (key && keysToRaplace[key]) {
      label = keysToRaplace[key];
      toTranslate = false;
    }

    const title = toTranslate ? this.translate.instant(label) : label;
    return [label, title, toTranslate];
  }
}
